// A7 «Shikoyatlar va tiketlar» — module-local data layer (does NOT touch
// `lib/adapters.ts` / `i18n/*` — HOT). REAL slices reuse @/lib/adapters directly:
//   • Shikoyatlar (complaints) → /admin/reports  (fetchReports/fetchReport/resolveReport)
//   • Tiketlar (tickets)       → /admin/tickets   (fetchTickets/replyTicket/setTicketStatus)
//   • Makroslar (macros)       → /admin/canned-responses (../canned/cannedApi)
//   • Hisobotlar (reports)     → /admin/analytics/support (SupportKpi below)
// Greenfield slices (assign/priority/tags/merge/notes/kb/auto-replies/csat/agents)
// fail-soft to empty via safeList/safeObj; mutations POST when the backend ships.
// Registration = ./REGISTER.md (C applies routes/screenStatus/nav/i18n/perm).
import { api } from '@/lib/api'
import type { FetchParams, FetchResult } from '@/components/DataTable/types'
import type { AdminReport } from '@/lib/adapters'

/* ── fail-soft primitives (devApi naqshi) ──────────────────────────── */

export function toResult<T>(rows: T[], params: FetchParams, search?: (r: T) => string): FetchResult<T> {
  const q = params.search.trim().toLowerCase()
  const filtered = q && search ? rows.filter((r) => search(r).toLowerCase().includes(q)) : rows
  const start = params.page * params.pageSize
  return { rows: filtered.slice(start, start + params.pageSize), total: filtered.length }
}

export async function safeList<T>(
  path: string,
  key: string,
  signal?: AbortSignal,
  query?: Record<string, string | number | undefined>,
): Promise<T[]> {
  try {
    const data = await api.get<Record<string, T[]>>(path, {
      ...(query ? { query } : {}),
      ...(signal ? { signal } : {}),
    })
    return (data?.[key] as T[] | undefined) ?? []
  } catch {
    return []
  }
}

export async function safeObj<T>(path: string, signal?: AbortSignal): Promise<T | null> {
  try {
    return await api.get<T>(path, signal ? { signal } : {})
  } catch {
    return null
  }
}

/* ── A7.05 Asossiz (dismissed) shikoyatlar — REAL /admin/reports?status ── */

/** Rad etilgan (asossiz) shikoyatlar: resolveReport dismiss=true → status 'dismissed'. */
export async function fetchUnfoundedComplaints(params: FetchParams): Promise<FetchResult<AdminReport>> {
  const rows = await safeList<AdminReport>('/admin/reports', 'reports', params.signal, {
    status: 'dismissed',
    limit: 300,
  })
  const only = rows.filter((r) => r.status === 'dismissed' || r.status === 'rejected')
  return toResult(only, params, (r) => `${r.category} ${r.reason ?? ''} ${r.resolution ?? ''}`)
}

/* ── A7.09 Tayinlash — REAL list + greenfield assign ───────────────── */

/** Greenfield: haydovchi/agentga tiket biriktirish. POST /admin/tickets/:id/assign. */
export const assignTicket = (id: number, agentId: number | null) =>
  api.post(`/admin/tickets/${id}/assign`, { assigned_to: agentId })

/* ── A7.10 Prioritet — REAL list + greenfield priority ─────────────── */

export const TICKET_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const
/** Greenfield: tiket muhimligini o'zgartirish. POST /admin/tickets/:id/priority. */
export const setTicketPriority = (id: number, priority: string) =>
  api.post(`/admin/tickets/${id}/priority`, { priority })

/* ── A7.11 Teg/kategoriyalar — greenfield tags ─────────────────────── */

export interface TicketTag {
  id: number
  name: string
  color?: string | null
  ticket_count: number
}
export const fetchTicketTags = (s?: AbortSignal) => safeList<TicketTag>('/admin/ticket-tags', 'tags', s)
export const createTicketTag = (name: string) => api.post<{ id: number }>('/admin/ticket-tags', { name })
export const deleteTicketTag = (id: number) => api.del(`/admin/ticket-tags/${id}`)

/* ── A7.12 Birlashtirish (duplikat) — greenfield merge ─────────────── */

/** Greenfield: duplikat tiketni asosiyga birlashtirish. POST /admin/tickets/merge. */
export const mergeTickets = (sourceId: number, targetId: number) =>
  api.post('/admin/tickets/merge', { source_id: sourceId, target_id: targetId })

/* ── A7.18 Ichki muhokama — greenfield notes ───────────────────────── */

export interface TicketNote {
  id: number
  ticket_id: number
  author?: string | null
  body: string
  created_at: number
}
export const fetchTicketNotes = (ticketId: number, s?: AbortSignal) =>
  safeList<TicketNote>(`/admin/tickets/${ticketId}/notes`, 'notes', s)
export const addTicketNote = (ticketId: number, body: string) =>
  api.post<{ id: number }>(`/admin/tickets/${ticketId}/notes`, { body })

/* ── A7.19 Bilim bazasi — greenfield KB ────────────────────────────── */

export interface KbArticle {
  id: number
  title: string
  category: string
  body: string
  views: number
  updated_at: number
}
export const fetchKbArticles = (s?: AbortSignal) => safeList<KbArticle>('/admin/kb', 'articles', s)
export const createKbArticle = (b: { title: string; category: string; body: string }) =>
  api.post<{ id: number }>('/admin/kb', b)
export const updateKbArticle = (id: number, b: { title: string; category: string; body: string }) =>
  api.put(`/admin/kb/${id}`, b)
export const deleteKbArticle = (id: number) => api.del(`/admin/kb/${id}`)

/* ── A7.20 Avtomatik javoblar — greenfield ─────────────────────────── */

export interface AutoReplyRule {
  id: number
  trigger: string
  match_type: 'contains' | 'equals' | 'regex'
  response: string
  enabled: boolean
  hits: number
}
export const fetchAutoReplies = (s?: AbortSignal) => safeList<AutoReplyRule>('/admin/auto-replies', 'rules', s)
export const createAutoReply = (b: { trigger: string; match_type: string; response: string }) =>
  api.post<{ id: number }>('/admin/auto-replies', b)
export const toggleAutoReply = (id: number, enabled: boolean) =>
  api.post(`/admin/auto-replies/${id}/toggle`, { enabled })
export const deleteAutoReply = (id: number) => api.del(`/admin/auto-replies/${id}`)

/* ── A7.21 CSAT — greenfield ───────────────────────────────────────── */

export interface CsatResponseRow {
  id: number
  ticket_id: number
  score: number
  comment?: string | null
  created_at: number
}
export interface CsatSummary {
  avg_score: number
  responses: number
  distribution: { score: number; count: number }[]
  recent: CsatResponseRow[]
}
export const fetchCsat = (s?: AbortSignal) => safeObj<CsatSummary>('/admin/csat', s)

/* ── A7.22 Agent statistikasi — greenfield ─────────────────────────── */

export interface AgentStatRow {
  agent_id: number
  name: string
  assigned: number
  resolved: number
  avg_response_min: number
  csat: number
}
export const fetchAgentStats = (s?: AbortSignal) => safeList<AgentStatRow>('/admin/support/agents', 'agents', s)

/* ── A7.24 Tiket hisobotlari — REAL /admin/analytics/support (A16.38) ── */

export interface SupportKpi {
  total: number
  open: number
  pending: number
  closed: number
  new_7d: number
  closed_7d: number
  avg_resolution_hours: number
  median_resolution_hours: number
  by_category: { key: string; count: number }[]
  by_priority: { key: string; count: number }[]
  new_per_day: { day: string; count: number }[]
}
export const fetchSupportKpi = (s?: AbortSignal) => safeObj<SupportKpi>('/admin/analytics/support', s)

/* ── Module-local RU strings (i18n HOT → keyга ko'chiriladi, REGISTER.md) ── */

export const TK_STR = {
  // umumiy
  save: 'Сохранить',
  cancel: 'Отмена',
  create: 'Создать',
  add: 'Добавить',
  edit: 'Изменить',
  delete: 'Удалить',
  done: 'Готово',
  failed: 'Не удалось',
  empty: 'Нет данных',
  loading: 'Загрузка…',
  apply: 'Применить',
  refresh: 'Обновить',
  back: 'Назад',
  category: 'Категория',
  status: 'Статус',
  priority: 'Приоритет',
  ticket: 'Тикет',
  ticketId: 'ID тикета',
  updated: 'Обновлён',
  created: 'Создан',
  user: 'Пользователь',
  count: 'Кол-во',
  greenfield: 'Модуль ждёт backend — данные появятся после включения эндпоинта',
  // A7.01/02/05 shikoyatlar
  complaints: 'Список жалоб',
  complaintsNote: 'Жалобы пользователей (GET /admin/reports) — категория, статус, объект',
  complaint: 'Карточка жалобы',
  reporter: 'Заявитель',
  target: 'Объект',
  reason: 'Причина',
  resolution: 'Решение',
  resolve: 'Обоснованно (закрыть)',
  dismiss: 'Отклонить (необоснованно)',
  resolvePlaceholder: 'Комментарий к решению…',
  unfounded: 'Необоснованные жалобы',
  unfoundedNote: 'Отклонённые жалобы — злоупотребление системой жалоб',
  notFound: 'Не найдено',
  // A7.08 yozishmalar
  history: 'История переписки',
  historyNote: 'Все тикеты по времени последней активности — откройте карточку для переписки',
  lastActivity: 'Последняя активность',
  subject: 'Тема',
  open_: 'Открыть',
  // A7.09 tayinlash
  assign: 'Назначение и очередь',
  assignNote: 'Распределение тикетов между агентами (assigned_to)',
  assignTo: 'Назначить агенту',
  unassigned: 'Не назначено',
  assigned: 'Назначено',
  assignDone: 'Тикет назначен',
  agent: 'Агент',
  // A7.10 prioritet
  priorityBoard: 'Управление приоритетом',
  priorityNote: 'Приоритет тикетов — от низкого до срочного',
  priorityDone: 'Приоритет обновлён',
  low: 'Низкий',
  normal: 'Обычный',
  high: 'Высокий',
  urgent: 'Срочный',
  // A7.11 teg
  tags: 'Теги и категории',
  tagsNote: 'Категории из тикетов (живые) + управляемые теги',
  liveCategories: 'Категории (из тикетов)',
  manageTags: 'Теги',
  tagName: 'Название тега',
  tagCreated: 'Тег создан',
  // A7.12 birlashtirish
  merge: 'Объединение дубликатов',
  mergeNote: 'Похожие тикеты одного пользователя — объедините в основной',
  mergeGroup: 'Группа дубликатов',
  mergeInto: 'Объединить в этот',
  mergeDone: 'Тикеты объединены',
  primary: 'Основной',
  noDuplicates: 'Дубликаты не найдены',
  // A7.16 makroslar
  macros: 'Макросы (ответ + действие)',
  macrosNote: 'Шаблон ответа (canned) + смена статуса в один клик',
  template: 'Шаблон',
  targetTicket: 'ID тикета',
  thenStatus: 'Затем статус',
  runMacro: 'Выполнить',
  macroDone: 'Макрос выполнен',
  pickTemplate: 'Выберите шаблон',
  noTemplates: 'Нет шаблонов ответов',
  // A7.18 ichki muhokama
  notes: 'Внутреннее обсуждение',
  notesNote: 'Приватные заметки агентов внутри тикета (не видны пользователю)',
  pickTicket: 'Выберите тикет',
  notePlaceholder: 'Внутренняя заметка…',
  noteAdded: 'Заметка добавлена',
  // A7.19 bilim bazasi
  kb: 'База знаний',
  kbNote: 'Статьи для агентов и авто-ответов',
  kbTitle: 'Заголовок',
  kbBody: 'Текст статьи',
  views: 'Просмотров',
  // A7.20 avto javob
  auto: 'Автоматические ответы',
  autoNote: 'Триггер → ответ. Срабатывает на новых тикетах',
  trigger: 'Триггер',
  matchType: 'Тип совпадения',
  response: 'Ответ',
  enabled: 'Включён',
  disabled: 'Выключен',
  hits: 'Срабатываний',
  contains: 'содержит',
  equals: 'равно',
  regex: 'regex',
  // A7.21 CSAT
  csat: 'Опрос CSAT',
  csatNote: 'Удовлетворённость поддержкой (оценки 1–5)',
  avgScore: 'Средняя оценка',
  responses: 'Ответов',
  distribution: 'Распределение оценок',
  recent: 'Последние отзывы',
  comment: 'Комментарий',
  // A7.22 agent stat
  agents: 'Статистика агентов',
  agentsNote: 'Назначено / решено / время ответа / CSAT по агентам',
  resolved: 'Решено',
  avgResponse: 'Ср. ответ (мин)',
  // A7.23 jamoa yuklamasi
  workload: 'Загрузка команды',
  workloadNote: 'Открытые тикеты на агента (по assigned_to, живые данные)',
  openTickets: 'Открытых тикетов',
  totalOpen: 'Всего открытых',
  agentsCount: 'Агентов',
  // A7.24 hisobotlar
  reports: 'Отчёты по тикетам',
  reportsNote: 'Сводный KPI поддержки (GET /admin/analytics/support)',
  total: 'Всего тикетов',
  openLbl: 'Открытых',
  pendingLbl: 'В ожидании',
  closedLbl: 'Закрыто',
  new7d: 'Новых за 7 дней',
  closed7d: 'Закрыто за 7 дней',
  avgRes: 'Ср. время решения',
  medianRes: 'Медиана решения',
  hours: 'ч',
  byCategory: 'По категориям',
  byPriority: 'По приоритету',
  newPerDay: 'Новые тикеты (30 дней)',
} as const
